/**
 * AIScopingAssistantPage - Support AI Full Page
 * P0: Two-tab UI (Advisory/Product Support), Role Selector, Fixed Project Dropdown
 * P0: Acceptance & KPIs step integration
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles, Send, RefreshCw, Crown, MessageSquare, User, Bot,
  Copy, Check, FileText, AlertTriangle, X, ChevronRight, Target,
  Lightbulb, HelpCircle, Clock, Settings, Users, Upload, Bell,
  ExternalLink, ChevronDown, Loader2, Briefcase, CheckCircle
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useHierarchy } from '../context/HierarchyContext';
import { useSupportContext, SUPPORT_ROLES } from '../context/SupportContext';
import { useProductMode } from '../context/ProductModeContext';
import ScopingModeNotice from '../components/ScopingModeNotice';
import ServicesRequestForm from '../components/ServicesRequestForm';
import AcceptanceKPICard from '../components/AcceptanceKPICard';
import ProactiveSuggestionsBanner from '../components/ProactiveSuggestionsBanner';
import { toast } from 'sonner';

// Tab definitions - added Acceptance & KPIs tab
const TABS = [
  { id: 'advisory', label: 'Advisory', icon: Sparkles, description: 'Scope, estimation & best practices' },
  { id: 'acceptance', label: 'Acceptance & KPIs', icon: CheckCircle, description: 'AI-suggested criteria & metrics' },
  { id: 'product', label: 'Product Support', icon: Settings, description: 'Setup, troubleshooting & configuration' }
];

// Suggested prompts for Advisory tab
const ADVISORY_PROMPTS = [
  {
    category: 'Scope Refinement',
    icon: Target,
    prompts: [
      'What are the key risks in this scope that need mitigation plans?',
      'Suggest additional deliverables we might have missed.',
      'What dependencies should we consider between these items?',
      'Help me break down this epic into more granular features.',
    ]
  },
  {
    category: 'Estimation',
    icon: Clock,
    prompts: [
      'What is a realistic timeline for delivering this scope?',
      'What team composition would you recommend for this project?',
      'Identify items that could be parallelized vs sequential.',
      'Which items have the highest complexity and why?',
    ]
  },
  {
    category: 'Best Practices',
    icon: Lightbulb,
    prompts: [
      'What governance checkpoints should we include?',
      'Suggest acceptance criteria for the key deliverables.',
      'What quality gates would you recommend?',
      'How should we handle scope changes during delivery?',
    ]
  },
];

// Suggested prompts for Product Support tab
const PRODUCT_PROMPTS = [
  {
    category: 'Users & Permissions',
    icon: Users,
    prompts: [
      'How do I add a new user and assign them a role?',
      'What permissions does each role have?',
      'How do I give someone access to a specific project?',
      'How do I set up user groups?',
    ]
  },
  {
    category: 'Upload & Import',
    icon: Upload,
    prompts: [
      'How do I fix "Parent not found" errors in my upload?',
      'What columns are required in the Excel template?',
      'How do I import hierarchical data correctly?',
      'Why is my upload failing with date format errors?',
    ]
  },
  {
    category: 'Integrations & Signals',
    icon: Bell,
    prompts: [
      'How do I configure Microsoft Teams notifications?',
      'How do I set up Daily Signal for my team?',
      'How do I connect WhatsApp Business?',
      'How do I configure SharePoint sync?',
    ]
  },
];

// Product Owner specific prompts
const PRODUCT_OWNER_PROMPTS = [
  {
    category: 'Scope Discovery',
    icon: Target,
    prompts: [
      'How do I structure scope from stakeholder interviews?',
      'What categories should I use for NFR vs functional requirements?',
      'How do I capture and link business decisions to scope?',
      'Best practices for conducting a scope workshop.',
    ]
  },
  {
    category: 'Documentation',
    icon: FileText,
    prompts: [
      'How do I attach and version documents to scope items?',
      'How do I request a sign-off on a scope document?',
      'What is the document approval workflow?',
      'How do I track document version history?',
    ]
  },
  {
    category: 'Governance',
    icon: Crown,
    prompts: [
      'How do I create a candidate baseline from approved decisions?',
      'What triggers scope drift alerts?',
      'How do I configure Daily Signal for scope changes?',
      'How do I use the e-signature workflow for approvals?',
    ]
  },
];

// Message component
const ChatMessage = ({ message }) => {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser 
          ? 'bg-[var(--clr-brand-primary)] text-white' 
          : 'bg-gradient-to-br from-purple-500 to-blue-500 text-white'
      }`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block p-4 rounded-2xl ${
          isUser 
            ? 'bg-[var(--clr-brand-primary)] text-white rounded-tr-sm' 
            : 'bg-gray-100 text-gray-800 rounded-tl-sm'
        }`}>
          <div className="whitespace-pre-wrap text-sm">{message.content}</div>
        </div>

        {!isUser && (
          <div className="mt-1 flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <span className="text-xs text-gray-400">
              {new Date(message.timestamp).toLocaleTimeString()}
            </span>
          </div>
        )}

        {/* Quick Actions */}
        {message.quickActions && message.quickActions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (action.action === 'link' && action.params?.href) {
                    window.location.href = action.params.href;
                  }
                }}
                className="text-xs px-2.5 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Citations */}
        {message.citations && message.citations.length > 0 && (
          <div className="mt-2 text-xs text-gray-500">
            <span className="font-medium">Sources:</span>{' '}
            {message.citations.map((c, idx) => (
              <span key={idx}>{c.title}{idx < message.citations.length - 1 ? ', ' : ''}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const AIScopingAssistantPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { mode: hierarchyMode } = useHierarchy();
  const { isScopingOnly } = useProductMode();
  const { 
    preferredRole, setPreferredRole, 
    selectedProjectId, setSelectedProjectId,
    buildSupportContext
  } = useSupportContext();
  const messagesEndRef = useRef(null);
  
  // Track if redirected from guard
  const [showGuardNotice, setShowGuardNotice] = useState(true);

  // State
  const [capabilities, setCapabilities] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [scopes, setScopes] = useState([]);
  const [selectedScope, setSelectedScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [activeTab, setActiveTab] = useState('advisory');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showServicesForm, setShowServicesForm] = useState(false);
  
  // Acceptance & KPIs state
  const [projectAcceptance, setProjectAcceptance] = useState([]);
  const [projectKPIs, setProjectKPIs] = useState([]);

  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  // Get prompts based on role and tab
  const currentPrompts = useMemo(() => {
    if (activeTab === 'product') {
      return PRODUCT_PROMPTS;
    }
    if (activeTab === 'acceptance') {
      return []; // No prompts for acceptance tab
    }
    // For Product Owner role in Advisory tab, use PO-specific prompts
    if (preferredRole === 'product_owner') {
      return PRODUCT_OWNER_PROMPTS;
    }
    return ADVISORY_PROMPTS;
  }, [activeTab, preferredRole]);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load initial data
  useEffect(() => {
    loadCapabilities();
    loadProjects();
    loadPreferences();
  }, [tenantId]);

  useEffect(() => {
    if (selectedProjectId) {
      loadScopes();
    }
  }, [selectedProjectId]);

  const loadCapabilities = async () => {
    if (!tenantId) return;
    try {
      const res = await api.getScopingCapabilities(tenantId);
      setCapabilities(res.data.capabilities);
    } catch (err) {
      console.error('Failed to load capabilities:', err);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Use new Support API for RBAC-respecting project list
  const loadProjects = async () => {
    if (!tenantId || !user?.id) return;
    setLoadingProjects(true);
    try {
      const res = await api.getSupportProjects(tenantId, user.id);
      setProjects(res.data.projects || []);

      // Check URL param first
      const projectIdParam = searchParams.get('project');
      if (projectIdParam) {
        const project = res.data.projects?.find(p => p.id === projectIdParam);
        if (project) {
          setSelectedProjectId(project.id);
        }
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      // Fallback to old API if support API fails
      try {
        const fallback = await api.getScopingProjects(tenantId);
        setProjects(fallback.data.projects || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadPreferences = async () => {
    if (!tenantId || !user?.id) return;
    try {
      const res = await api.getSupportPreferences(tenantId, user.id);
      if (res.data.preferred_role && !preferredRole) {
        setPreferredRole(res.data.preferred_role);
      }
      if (res.data.last_project_id && !selectedProjectId) {
        setSelectedProjectId(res.data.last_project_id);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const savePreferences = async (role, projectId) => {
    if (!tenantId || !user?.id) return;
    try {
      await api.updateSupportPreferences(tenantId, user.id, {
        preferred_role: role,
        last_project_id: projectId
      });
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
  };

  const loadScopes = async () => {
    if (!tenantId || !selectedProjectId) return;
    try {
      const res = await api.listBaselines({
        tenant_id: tenantId,
        project_id: selectedProjectId
      });
      setScopes(res.data.baselines || []);
      if (res.data.baselines?.length > 0) {
        setSelectedScope(res.data.baselines[0]);
      } else {
        setSelectedScope(null);
      }
      
      // Also load project acceptance/KPIs
      loadProjectGovernanceData();
    } catch (err) {
      console.error('Failed to load scopes:', err);
    }
  };
  
  // Load project acceptance criteria and KPIs
  const loadProjectGovernanceData = async () => {
    if (!tenantId || !selectedProjectId) return;
    try {
      const res = await api.getProjectDashboard(selectedProjectId);
      setProjectAcceptance(res.data?.project?.acceptanceCriteria || []);
      setProjectKPIs(res.data?.project?.kpis || []);
    } catch (err) {
      console.error('Failed to load project governance data:', err);
    }
  };
  
  // Handle save from AcceptanceKPICard
  const handleAcceptanceKPISave = ({ acceptance, kpis }) => {
    setProjectAcceptance(acceptance);
    setProjectKPIs(kpis);
    toast.success('Acceptance Criteria and KPIs saved successfully');
  };

  const handleRoleChange = (roleKey) => {
    setPreferredRole(roleKey);
    setShowRoleDropdown(false);
    savePreferences(roleKey, selectedProjectId);
    toast.success(`Role set to ${SUPPORT_ROLES.find(r => r.key === roleKey)?.label}`);
    
    // Track role selection telemetry
    import('../utils/telemetry').then(({ trackEvent, TELEMETRY_EVENTS }) => {
      trackEvent(TELEMETRY_EVENTS.SUPPORT_AI_ROLE_SELECTED, { role: roleKey });
    });
  };

  const handleProjectChange = (projectId) => {
    setSelectedProjectId(projectId);
    setSelectedScope(null);
    setMessages([]);
    setShowSuggestions(true);
    savePreferences(preferredRole, projectId);
  };

  // Send message using Support AI API
  const handleSend = async (promptText = inputValue) => {
    if (!promptText.trim() || sending) return;

    const userMessage = {
      role: 'user',
      content: promptText.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setSending(true);
    setShowSuggestions(false);
    setError(null);

    try {
      const supportContext = buildSupportContext({
        projectId: selectedProjectId,
        baselineStatus: selectedScope?.status
      });

      const res = await api.supportAssist(tenantId, user.id, {
        mode: activeTab,
        message: promptText.trim(),
        supportContext
      });

      const assistantMessage = {
        role: 'assistant',
        content: res.data.answerHtml,
        timestamp: new Date().toISOString(),
        quickActions: res.data.quickActions,
        links: res.data.links,
        citations: res.data.citations
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Assistant call failed:', err);
      const errorMsg = err.response?.data?.detail?.message || 
                       err.response?.data?.detail || 
                       'Failed to get response from AI assistant';
      setError(errorMsg);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I encountered an error: ${errorMsg}. Please try again.`,
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setShowSuggestions(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Tier gate - Enterprise/Business required
  if (!capabilities?.ai_assistant) {
    return (
      <div className="p-8" data-testid="tier-gate">
        <div className="max-w-lg mx-auto text-center">
          <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Support AI Requires Enterprise Tier
          </h2>
          <p className="text-gray-600 mb-6">
            Upgrade to Enterprise to access the AI-powered support assistant for intelligent scope refinement, estimation guidance, setup help, and best practice recommendations.
          </p>
          <button
            onClick={() => navigate('/admin/billing')}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            Upgrade to Enterprise
          </button>
        </div>
      </div>
    );
  }

  const currentRole = SUPPORT_ROLES.find(r => r.key === preferredRole);
  const currentProject = projects.find(p => p.id === selectedProjectId);
  const prompts = currentPrompts;  // Use the dynamic prompts based on role/tab

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="ai-support-assistant-page">
      {/* Scoping Mode Notice - shown when redirected from route guard */}
      {showGuardNotice && (
        <div className="px-6 pt-4">
          <ScopingModeNotice onDismiss={() => setShowGuardNotice(false)} />
        </div>
      )}
      
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Support AI</h1>
              <p className="text-sm text-gray-500">
                Intelligent assistance for scope, setup, and troubleshooting
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Role Selector */}
            <div className="relative">
              <button
                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:border-gray-400 min-w-[160px]"
                data-testid="role-selector"
              >
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700">{currentRole?.label || 'Select role...'}</span>
                <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
              </button>
              
              {showRoleDropdown && (
                <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                    I am a...
                  </div>
                  {SUPPORT_ROLES.map(role => (
                    <button
                      key={role.key}
                      onClick={() => handleRoleChange(role.key)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 ${
                        preferredRole === role.key ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className={`text-sm font-medium ${preferredRole === role.key ? 'text-purple-700' : 'text-gray-800'}`}>
                        {role.label}
                      </div>
                      <div className="text-xs text-gray-500">{role.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Project selector */}
            <select
              value={selectedProjectId || ''}
              onChange={(e) => handleProjectChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none min-w-[200px]"
              data-testid="project-selector"
            >
              <option value="">Select Project</option>
              {loadingProjects ? (
                <option disabled>Loading projects...</option>
              ) : projects.length === 0 ? (
                <option disabled>No projects available</option>
              ) : (
                projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))
              )}
            </select>

            {/* Scope selector */}
            {scopes.length > 0 && (
              <select
                value={selectedScope?.id || ''}
                onChange={(e) => {
                  const scope = scopes.find(s => s.id === e.target.value);
                  setSelectedScope(scope);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                data-testid="scope-selector"
              >
                <option value="">Select Baseline</option>
                {scopes.map(s => (
                  <option key={s.id} value={s.id}>V{s.version} - {s.status}</option>
                ))}
              </select>
            )}

            {/* Clear chat */}
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm transition-colors"
                data-testid="clear-chat-btn"
              >
                Clear Chat
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg max-w-md">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMessages([]);
                setShowSuggestions(true);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty State for No Projects */}
      {!loadingProjects && projects.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Projects Yet</h3>
            <p className="text-gray-600 mb-6">
              You don't have access to any projects. Create a new project or request access from your administrator.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/projects?action=create')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
              >
                Create Project
              </button>
              <button
                onClick={() => navigate('/admin/users')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Request Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat area */}
      {(projects.length > 0 || loadingProjects) && (
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-4xl mx-auto py-6 px-4">
            {/* Context banner */}
            {selectedScope && (
              <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      Context: {currentProject?.name} - Baseline V{selectedScope.version}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedScope.ai_extraction?.summary || 'No summary available'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{selectedScope.ai_structure?.length || 0} items</span>
                      <span>{selectedScope.ai_extraction?.objectives?.length || 0} objectives</span>
                      <span>Status: <span className="capitalize">{selectedScope.status}</span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Welcome / suggestions - only for chat tabs */}
            {showSuggestions && messages.length === 0 && activeTab !== 'acceptance' && (
              <div className="mb-6">
                <div className="text-center mb-8">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">
                    {activeTab === 'advisory' 
                      ? 'How can I help with your scope?'
                      : 'What do you need help with?'}
                  </h2>
                  <p className="text-gray-600 text-sm">
                    {activeTab === 'advisory'
                      ? 'Ask about scope refinement, estimation, or best practices.'
                      : 'Get help with setup, permissions, uploads, and integrations.'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {prompts.map((category, idx) => (
                    <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <category.icon className="w-5 h-5 text-purple-500" />
                        <h3 className="font-medium text-gray-800">{category.category}</h3>
                      </div>
                      <div className="space-y-2">
                        {category.prompts.map((prompt, pIdx) => (
                          <button
                            key={pIdx}
                            onClick={() => handleSend(prompt)}
                            className="w-full text-left p-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-colors"
                            data-testid={`suggested-prompt-${idx}-${pIdx}`}
                          >
                            <ChevronRight className="w-3 h-3 inline mr-1" />
                            {prompt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Services Request Quick Action - shown in Product Support tab */}
                {activeTab === 'product' && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Need Professional Services?</h4>
                          <p className="text-sm text-gray-600">
                            Get help from our Implementation PM, BA Scoping, or Integration experts.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowServicesForm(true)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                        data-testid="request-services-btn"
                      >
                        Request Services
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Product Owner role tip */}
                {preferredRole === 'product_owner' && activeTab === 'advisory' && (
                  <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <Crown className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <span className="font-medium">Product Owner Focus:</span>{' '}
                      Ask about discovery → scope structuring → decisions linkage → candidate baseline → doc sign-offs workflow.
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Acceptance & KPIs Tab Content */}
            {activeTab === 'acceptance' && selectedProjectId && (
              <div className="space-y-6">
                {/* Proactive Suggestions Banner */}
                <ProactiveSuggestionsBanner
                  context="acceptance_kpis"
                  entityId={selectedProjectId}
                  entityType="project"
                  className="mb-4"
                />
                
                {/* AcceptanceKPICard in step variant */}
                <AcceptanceKPICard
                  projectId={selectedProjectId}
                  demandSummary={selectedScope?.ai_extraction?.summary || currentProject?.description}
                  initialAcceptance={projectAcceptance}
                  initialKPIs={projectKPIs}
                  onSave={handleAcceptanceKPISave}
                  variant="step"
                  className="max-w-[1040px]"
                />
              </div>
            )}
            
            {/* No project selected for Acceptance tab */}
            {activeTab === 'acceptance' && !selectedProjectId && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Project</h3>
                <p className="text-gray-600 text-sm max-w-md mx-auto">
                  Choose a project from the dropdown above to manage AI-suggested Acceptance Criteria and KPIs.
                </p>
              </div>
            )}

            {/* Messages - only for chat tabs */}
            {activeTab !== 'acceptance' && (
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <ChatMessage key={idx} message={msg} />
                ))}
              
                {/* Typing indicator */}
                {sending && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Input area - only show for chat tabs */}
      {(projects.length > 0 || loadingProjects) && activeTab !== 'acceptance' && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white p-4">
          <div className="max-w-4xl mx-auto">
            {/* Error display */}
            {error && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
                <button onClick={() => setError(null)} className="ml-auto">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex gap-3">
              <div className="flex-1 relative">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={activeTab === 'advisory'
                    ? "Ask about scope, estimation, or best practices..."
                    : "Ask about setup, permissions, uploads, or integrations..."
                  }
                  rows={2}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
                  disabled={sending}
                  data-testid="chat-input"
                />
                <button
                  onClick={() => handleSend()}
                  disabled={!inputValue.trim() || sending}
                  className="absolute right-2 bottom-2 p-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                  data-testid="send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>Press Enter to send, Shift+Enter for new line</span>
              <span className="flex items-center gap-1">
                <HelpCircle className="w-3 h-3" />
                AI responses are suggestions - always verify with your team
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Services Request Modal */}
      <ServicesRequestForm
        isOpen={showServicesForm}
        onClose={() => setShowServicesForm(false)}
        onSuccess={() => setShowServicesForm(false)}
      />
    </div>
  );
};

export default AIScopingAssistantPage;
